import React, { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSessionQuery } from '../../../auth/hooks';
import { clientManagementApi } from '../api/clientManagement';
import type { LicenceRow } from '../api/clientManagement';
import { qk } from '../../../../lib/queryKeys';

export const LicencesList: React.FC = () => {
  const queryClient = useQueryClient();
  const { data: session } = useSessionQuery();
  const isCentralAdmin = session?.user?.is_central_admin ?? false;

  const [search, setSearch] = useState('');
  const [editRow, setEditRow] = useState<LicenceRow | null>(null);
  const [draft, setDraft] = useState('');

  const licencesQuery = useQuery({
    queryKey: qk.licences(),
    queryFn: () => clientManagementApi.listLicences(),
    enabled: isCentralAdmin,
    staleTime: 2 * 60 * 1000,
  });

  const updateMutation = useMutation({
    mutationFn: (payload: { clientId: string; licence_total: number | null }) =>
      clientManagementApi.updateClient(payload.clientId, {
        licence_total: payload.licence_total,
      }),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: qk.licences() });
      queryClient.invalidateQueries({ queryKey: qk.clientDetails(variables.clientId) });
      queryClient.invalidateQueries({ queryKey: qk.clientLicences(variables.clientId) });
      setEditRow(null);
    },
  });

  const openEdit = (row: LicenceRow) => {
    setEditRow(row);
    setDraft(row.total === null ? '' : String(row.total));
  };

  // Parse the dialog input: blank clears (null); otherwise a non-negative integer.
  const parsedDraft = (): { valid: boolean; value: number | null } => {
    const trimmed = draft.trim();
    if (trimmed === '') return { valid: true, value: null };
    const n = Number(trimmed);
    if (Number.isInteger(n) && n >= 0) return { valid: true, value: n };
    return { valid: false, value: null };
  };

  const draftState = parsedDraft();

  if (!isCentralAdmin) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity='info'>Central admin access required to view licences.</Alert>
      </Box>
    );
  }

  const rows = licencesQuery.data ?? [];
  const filtered = search ? rows.filter((r) => r.name.toLowerCase().includes(search.toLowerCase())) : rows;

  return (
    <Box sx={{ p: { xs: 2.5, md: 3 } }}>
      <Box mb={3}>
        <Typography variant='h5' fontWeight={600}>
          Licences
        </Typography>
        <Typography variant='body2' color='text.secondary' sx={{ mt: 0.5 }}>
          Allocated WHOOP licences per client. Active is the number of active users; vacant is what remains.
        </Typography>
      </Box>

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} mb={2} alignItems='center'>
        <TextField
          size='small'
          placeholder='Search clients…'
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ maxWidth: 320 }}
          fullWidth
        />
      </Stack>

      {licencesQuery.isLoading ? (
        <Stack direction='row' spacing={1} alignItems='center' sx={{ py: 4 }}>
          <CircularProgress size={20} />
          <Typography variant='body2'>Loading licences…</Typography>
        </Stack>
      ) : filtered.length === 0 ? (
        <Typography variant='body2' color='text.secondary'>
          {search ? 'No clients match your search.' : 'No clients found.'}
        </Typography>
      ) : (
        <TableContainer>
          <Table size='small'>
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell align='right'>Total</TableCell>
                <TableCell align='right'>Active</TableCell>
                <TableCell align='right'>Vacant</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {filtered.map((row) => {
                const oversubscribed = row.total !== null && row.active > row.total;
                return (
                  <TableRow key={row.clientId} hover>
                    <TableCell>
                      <Typography variant='body2' fontWeight={500}>
                        {row.name}
                      </Typography>
                    </TableCell>
                    <TableCell align='right'>
                      {row.total === null ? (
                        <Typography variant='body2' color='text.secondary'>
                          Not set
                        </Typography>
                      ) : (
                        row.total
                      )}
                    </TableCell>
                    <TableCell align='right'>{row.active}</TableCell>
                    <TableCell align='right'>
                      {oversubscribed ? (
                        <Chip label='Oversubscribed' size='small' color='error' variant='outlined' />
                      ) : row.available === null ? (
                        <Typography variant='body2' color='text.secondary'>
                          —
                        </Typography>
                      ) : (
                        row.available
                      )}
                    </TableCell>
                    <TableCell align='right'>
                      <Tooltip title='Edit total'>
                        <IconButton size='small' onClick={() => openEdit(row)}>
                          <EditOutlinedIcon fontSize='small' />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Dialog open={!!editRow} onClose={() => setEditRow(null)} fullWidth maxWidth='xs'>
        <DialogTitle>Edit licence total — {editRow?.name}</DialogTitle>
        <DialogContent>
          <Stack spacing={1.5} sx={{ mt: 1 }}>
            <TextField
              fullWidth
              size='small'
              type='number'
              label='Allocated total'
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              helperText='Leave blank to clear (not set). Must be a whole number ≥ 0.'
              error={!draftState.valid}
              inputProps={{ min: 0, step: 1 }}
              autoFocus
            />
            {updateMutation.isError && <Alert severity='error'>Failed to save. Please try again.</Alert>}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditRow(null)}>Cancel</Button>
          <Button
            variant='contained'
            disabled={!draftState.valid || updateMutation.isPending}
            onClick={() =>
              editRow && updateMutation.mutate({ clientId: editRow.clientId, licence_total: draftState.value })
            }
          >
            {updateMutation.isPending ? 'Saving…' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
